import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import NoEmployeesIcon from '@assets/icons/no-employees-icon.svg';
import React, { useMemo } from 'react';
import { ActivityIndicator } from '@/components/activity-indicator';
import { AddNewFooter } from '@/components/add-new-footer';
import {
  APP_MODAL_ROUTES,
  EMPLOYEE_MANAGEMENT_ROUTES,
  LOCATION_MANAGER_ROUTES,
} from '@/navigators/routes';
import {
  AppModalStackParams,
  LocationManagerStackParams,
  RestaurantStackParams,
} from '@/navigators/types';
import { Avatar } from '@/components/avatar';
import { Button } from '@/components/button';
import {
  CompositeNavigationProp,
  useNavigation,
} from '@react-navigation/native';
import { createStyles } from '@/contexts/theme-context';
import {
  EmployeeRestaurant,
  useAuthenticatedUserContext,
} from '@/contexts/user-context';
import { EmployeeStatus } from '@/enums';
import { EmptyStateMinimal } from '@/components/empty-state-minimal';
import { formatPhoneNumber, isNotMaliPhoneNumber } from '@/utils/phone-number';
import { List } from '@/components/list';
import { LIST_MARGIN_HORIZONTAL, SCREEN_PADDING, TYPOGRAPHY } from '@/styles';
import { ListItemSeparator, ListOption } from '@/components/list-item';
import { RestaurantEmployeeFragment } from '@/types';
import { StackNavigationProp } from '@react-navigation/stack';
import { StyleProp, Text, View, ViewStyle } from 'react-native';

export const EMPLOYEE_ROLE_TRANSLATION_MAP: { [role: string]: string } = {
  ADMIN: i18n.t('admin'),
  OTHER: i18n.t('other'),
  SUPPLIER_DEFAULT: i18n.t('default'),
  USER: i18n.t('user'),
};

type EmployeeListOption = ListOption<RestaurantEmployeeFragment>;

type Navigation = CompositeNavigationProp<
  CompositeNavigationProp<
    StackNavigationProp<
      RestaurantStackParams,
      EMPLOYEE_MANAGEMENT_ROUTES.Employees
    >,
    StackNavigationProp<
      AppModalStackParams,
      APP_MODAL_ROUTES.RestaurantInviteEmployee
    >
  >,
  StackNavigationProp<
    LocationManagerStackParams,
    LOCATION_MANAGER_ROUTES.InviteEmployee
  >
>;

interface Props {
  addNewItemStyle?: StyleProp<ViewStyle>;
  emailAddress?: string;
  employeeItemStyle?: StyleProp<ViewStyle>;
  hasItemDivider?: boolean;
  hideAddNew?: boolean;
  hideUpdateEmployees?: boolean;
  isEmployeeSelectable?: (employee?: RestaurantEmployeeFragment) => boolean;
  itemDividerStyle?: StyleProp<ViewStyle>;
  loading: boolean;
  onAddNew?: () => void;
  onComplete?: (employeeId: string) => void;
  onEmployeeSelect: (employee: RestaurantEmployeeFragment) => void;
  phoneNumber?: string;
  restaurantEmployees: RestaurantEmployeeFragment[];
  restaurantName?: string;
  restaurantRelationship?: EmployeeRestaurant;
  restaurantUUID?: string;
  scrollEnabled?: boolean;
  selectedEmployeeItemStyle?: StyleProp<ViewStyle>;
  selectedRestaurantEmployeeUUID?: string;
  style?: StyleProp<ViewStyle>;
  supplierUUID?: string;
}

const EmployeeListSeparator: React.FC<{ style?: StyleProp<ViewStyle> }> = ({
  style,
}) => {
  const [styles] = useStyles();
  return <ListItemSeparator style={[styles.employeeItemMargin, style]} />;
};

export const RestaurantEmployeeList: React.FC<Props> = ({
  addNewItemStyle,
  emailAddress,
  employeeItemStyle,
  hasItemDivider = true,
  hideAddNew = false,
  hideUpdateEmployees = false,
  isEmployeeSelectable = () => true,
  itemDividerStyle,
  loading,
  onAddNew,
  onComplete,
  onEmployeeSelect,
  phoneNumber,
  restaurantEmployees,
  restaurantName,
  restaurantRelationship,
  restaurantUUID,
  scrollEnabled,
  selectedEmployeeItemStyle,
  selectedRestaurantEmployeeUUID,
  style,
  supplierUUID,
}) => {
  const { navigate } = useNavigation<Navigation>();
  const {
    selectedRestaurantRelationship,
    selectedRestaurantUUID,
    supplierEmployeeRole,
  } = useAuthenticatedUserContext();
  const targetRestaurantRelationship =
    restaurantRelationship ?? selectedRestaurantRelationship;
  const targetRestaurantUUID =
    restaurantRelationship?.uuid ?? selectedRestaurantUUID;

  const [styles] = useStyles();

  const visibleRestaurantName =
    restaurantName || targetRestaurantRelationship.name;

  const canUpdateEmployees =
    targetRestaurantRelationship.employeeRole === 'ADMIN' ||
    supplierEmployeeRole;

  const navigateToAddNew = () => {
    if (restaurantName && restaurantUUID && supplierUUID) {
      const locationManagerParams = {
        initialEmailAddress: emailAddress,
        initialPhoneNumber: phoneNumber,
        onComplete,
        restaurantName,
        restaurantRelationship,
        restaurantUUID,
        supplierUUID,
      };
      if (onAddNew) {
        onAddNew();
      } else {
        navigate(LOCATION_MANAGER_ROUTES.InviteEmployee, locationManagerParams);
      }
    } else {
      const appModalParams = {
        initialEmailAddress: emailAddress,
        initialPhoneNumber: phoneNumber,
        onComplete,
        restaurantName: targetRestaurantRelationship.name,
        restaurantRelationship,
        restaurantUUID: targetRestaurantUUID,
      };
      if (onAddNew) {
        onAddNew();
      } else {
        navigate(APP_MODAL_ROUTES.RestaurantInviteEmployee, appModalParams);
      }
    }
  };

  const employees = useMemo(() => {
    return restaurantEmployees.map<EmployeeListOption>((relationship) => {
      const employeeContactPhoneNumber =
        relationship.employee_contact_phone_number &&
        isNotMaliPhoneNumber(relationship.employee_contact_phone_number)
          ? formatPhoneNumber(relationship.employee_contact_phone_number)
          : null;
      return {
        metadata: relationship,
        subtitle: (
          <>
            {employeeContactPhoneNumber ? (
              <Text style={[TYPOGRAPHY.small, styles.employeeSubtitle]}>
                {employeeContactPhoneNumber}
              </Text>
            ) : null}
            <Text
              style={[
                TYPOGRAPHY.small,
                styles.employeeSubtitle,
                styles.employeeStatus,
              ]}
            >
              {
                EMPLOYEE_ROLE_TRANSLATION_MAP[
                  relationship.restaurant_employee_role
                ]
              }
              {relationship.employee_status === EmployeeStatus.Invited
                ? ` · ${i18n.t('invited')}`
                : null}
            </Text>
          </>
        ),
        title: relationship.employee_name,
        value: relationship.employee_id,
      };
    }, []);
  }, [restaurantEmployees, styles]);

  return loading ? (
    <View style={styles.paddingTop}>
      <ActivityIndicator testID="loading-indicator" />
    </View>
  ) : (
    <List<EmployeeListOption>
      data={employees}
      ItemSeparatorComponent={() =>
        hasItemDivider ? (
          <EmployeeListSeparator style={itemDividerStyle} />
        ) : null
      }
      ListHeaderComponent={() => {
        if (employees.length === 0) {
          return (
            <EmptyStateMinimal
              containerStyle={styles.emptyStateContainer}
              ImageComponent={NoEmployeesIcon}
              text={i18n.t('employee_list_empty_state.title')}
              textStyle={TYPOGRAPHY.h2}
            >
              <Text style={[TYPOGRAPHY.medium, styles.emptyStateTitle]}>
                {i18n.t('employee_list_empty_state.subtitle')}
              </Text>
              {canUpdateEmployees && !hideUpdateEmployees ? (
                <Button
                  containerStyle={styles.emptyStateSubtitle}
                  onPress={navigateToAddNew}
                  textStyle={styles.emptyStateText}
                  trackingData={{
                    name: 'account_restaurant.add_employee',
                  }}
                >
                  {i18n.t('add_employee_to', {
                    selectedRestaurant: visibleRestaurantName,
                  })}
                </Button>
              ) : null}
            </EmptyStateMinimal>
          );
        }

        return canUpdateEmployees && !hideAddNew ? (
          <>
            <View style={styles.employeeItemMargin}>
              <AddNewFooter
                listItemStyle={[styles.employeeItemPadding, addNewItemStyle]}
                onSelect={() => {
                  Analytics.track('account_restaurant.add_employee.press');
                  navigateToAddNew();
                }}
                title={i18n.t('add_employee_to', {
                  selectedRestaurant: visibleRestaurantName,
                })}
              />
            </View>
            <EmployeeListSeparator style={itemDividerStyle} />
          </>
        ) : null;
      }}
      listItemStyle={(item) => {
        const disabledStyles = isEmployeeSelectable(item.metadata)
          ? null
          : styles.disabled;
        const selectedEmployeeStyles =
          item.metadata!.restaurant_employee_id ===
          selectedRestaurantEmployeeUUID
            ? [styles.selectedEmployee, selectedEmployeeItemStyle]
            : null;
        return [
          disabledStyles,
          styles.employeeItemMargin,
          styles.employeeItemPadding,
          selectedEmployeeStyles,
          employeeItemStyle,
        ];
      }}
      onSelect={({ metadata }) => {
        if (isEmployeeSelectable(metadata)) {
          onEmployeeSelect(metadata!);
        }
      }}
      renderLeftComponent={(option) => {
        return (
          <Avatar
            name={option.metadata!.employee_name}
            uuid={option.metadata!.employee_id}
          />
        );
      }}
      scrollEnabled={scrollEnabled}
      style={style}
    />
  );
};

const useStyles = createStyles((theme) => ({
  disabled: {
    marginHorizontal: 0,
    opacity: 0.4,
  },
  employeeItemMargin: {
    marginHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  employeeItemPadding: {
    paddingHorizontal: LIST_MARGIN_HORIZONTAL,
  },
  employeeStatus: {
    color: theme.colors.inactiveText,
  },
  employeeSubtitle: {
    lineHeight: 20,
  },
  emptyStateContainer: {
    backgroundColor: theme.colors.white,
    justifyContent: 'flex-start',
    paddingVertical: 48,
  },
  emptyStateSubtitle: {
    marginTop: 16,
    width: '100%',
  },
  emptyStateText: {
    textAlign: 'center',
  },
  emptyStateTitle: {
    lineHeight: 24,
    marginTop: 8,
    textAlign: 'center',
  },
  paddingTop: {
    paddingTop: SCREEN_PADDING,
  },
  selectedEmployee: {
    backgroundColor: theme.colors.accentLight,
  },
}));
